import { useCallback, useEffect, useRef, useState } from "react";
import {
  clearCreativeJobProgress,
  ensureCreativeJobProgressSession,
  getCreativeJobProgressState,
  migrateCreativeJobProgress,
  setCreativeJobProgress,
} from "../utils/creativeJobProgressStorage";
import {
  formatTaskEstimatedDuration,
  getTaskEstimatedDurationMs,
  getTaskRemainingDurationMs,
} from "../utils/taskEstimatedDurations";

/**
 * Persists loader progress + countdown for long-running creative/campaign tasks.
 *
 * @param {object} options
 * @param {string} options.storageKey Canonical persistence key (jobID or campaignID).
 * @param {string} [options.migrateFromKey] Temporary alert id to promote into storageKey.
 * @param {string} [options.jobType] Used to resolve estimated duration.
 * @param {boolean} options.isTerminal When true, clears persisted session.
 */
export const useCreativeJobProgressLoader = ({
  storageKey,
  migrateFromKey,
  jobType,
  isTerminal,
  itemCount,
}) => {
  const estimatedDurationMs = getTaskEstimatedDurationMs({ jobType, itemCount });
  const hasMigratedRef = useRef(false);

  const [session, setSession] = useState(() =>
    getCreativeJobProgressState({
      jobID: storageKey,
      jobType,
      estimatedDurationMs,
    })
  );
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (
      !migrateFromKey ||
      !storageKey ||
      migrateFromKey === storageKey ||
      hasMigratedRef.current
    ) {
      return;
    }

    migrateCreativeJobProgress({
      fromKey: migrateFromKey,
      toKey: storageKey,
      jobType,
      estimatedDurationMs,
    });
    hasMigratedRef.current = true;
    setSession(
      getCreativeJobProgressState({
        jobID: storageKey,
        jobType,
        estimatedDurationMs,
      })
    );
  }, [migrateFromKey, storageKey, jobType, estimatedDurationMs]);

  useEffect(() => {
    if (!storageKey || isTerminal) return;

    ensureCreativeJobProgressSession({
      jobID: storageKey,
      jobType,
      estimatedDurationMs,
    });

    setSession(
      getCreativeJobProgressState({
        jobID: storageKey,
        jobType,
        estimatedDurationMs,
      })
    );
  }, [storageKey, jobType, estimatedDurationMs, isTerminal]);

  useEffect(() => {
    if (!storageKey || isTerminal) return;

    const intervalId = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(intervalId);
  }, [storageKey, isTerminal]);

  useEffect(() => {
    if (!isTerminal) return;

    if (storageKey) clearCreativeJobProgress(storageKey);
    if (migrateFromKey) clearCreativeJobProgress(migrateFromKey);
  }, [isTerminal, storageKey, migrateFromKey]);

  const onProgressChange = useCallback(
    (value) => {
      if (!storageKey) return;

      const nextProgress = Math.min(99, Math.max(0, Number(value) || 0));

      setCreativeJobProgress({
        jobID: storageKey,
        progress: nextProgress,
        jobType,
        estimatedDurationMs,
      });

      // Bump local progress only — do not re-read storage with Date.now().
      // Recomputing elapsed on every tick feeds a new seedProgress float back
      // into LinearLoaderWithTime and causes Maximum update depth exceeded.
      setSession((prev) => {
        if (nextProgress <= (Number(prev.progress) || 0)) {
          return prev;
        }
        return {
          ...prev,
          progress: nextProgress,
        };
      });
    },
    [storageKey, jobType, estimatedDurationMs]
  );

  const sessionDurationMs =
    session.estimatedDurationMs || estimatedDurationMs;

  const remainingMs = getTaskRemainingDurationMs({
    startedAt: session.startedAt,
    estimatedDurationMs: sessionDurationMs,
    now,
  });

  let seedProgress = Math.min(99, Math.max(0, Number(session.progress) || 0));
  if (session.startedAt && sessionDurationMs > 0) {
    const elapsedProgress =
      ((now - session.startedAt) / sessionDurationMs) * 100;
    seedProgress = Math.min(99, Math.max(seedProgress, elapsedProgress));
  }

  return {
    loaderTimeMs: estimatedDurationMs,
    onProgressChange,
    seedProgress,
    storageKey,
    remainingMs,
    formattedRemainingTime: formatTaskEstimatedDuration({ remainingMs }),
    startedAt: session.startedAt,
    estimatedDurationMs: sessionDurationMs,
  };
};
